import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowRight, CheckCircle2, ChevronLeft, Sparkles } from "lucide-react";
import Header from "../components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /auth/forgot-password — requests a password-reset link via Resend.
 * Always returns 200 on the server regardless of whether the email
 * exists, so this page says the same friendly thing to everyone.
 */
const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!email.includes("@")) {
      toast.error(t("forgot.errInvalidEmail"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/password/forgot`, {
        email: email.trim().toLowerCase(),
        origin: window.location.origin,
        locale: i18n.language?.split("-")[0] || "en",
      });
      setSent(true);
    } catch {
      // Backend intentionally returns 200, but if something truly
      // catches fire we still show the same friendly "if it exists"
      // message — no information disclosure.
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />
      <section
        className="pt-32 pb-24 px-6 flex items-center justify-center min-h-[90vh]"
        data-testid="forgot-root"
      >
        <div className="max-w-md w-full">
          <div className="bg-white border border-[#EADDD7] rounded-3xl p-10 soft-shadow-lg">
            <div className="h-14 w-14 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center mb-6 soft-shadow">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            {sent ? (
              <div data-testid="forgot-sent-state">
                <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
                  {t("forgot.sentKicker")}
                </span>
                <h1 className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-tight mb-3">
                  {t("forgot.sentHeadline")}
                </h1>
                <p className="text-[#5C4B51] leading-relaxed mb-5">
                  {t("forgot.sentBody")}
                </p>
                <div className="bg-[#F5EFEB] rounded-2xl p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#9B6A6C] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#5C4B51] leading-relaxed">
                    {t("forgot.sentTip")}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
                  {t("forgot.kicker")}
                </span>
                <h1 className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-tight mb-3">
                  {t("forgot.headline")}
                </h1>
                <p className="text-[#5C4B51] mb-6 leading-relaxed">
                  {t("forgot.subline")}
                </p>
                <form onSubmit={onSubmit} className="space-y-3">
                  <div className="relative">
                    <Mail className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("login.emailPlaceholder")}
                      className="w-full bg-white border border-[#EADDD7] rounded-full pl-10 pr-4 py-3 text-[#2C2123] placeholder:text-[#8A7D82] focus:outline-none focus:ring-2 focus:ring-[#D5A8A4]/50 focus:border-[#D5A8A4] transition-all"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-60 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 transition-all duration-300"
                    data-testid="forgot-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        {t("forgot.sending")}
                      </>
                    ) : (
                      <>
                        {t("forgot.submit")}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
          <div className="mt-6 flex flex-col items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors"
              data-testid="forgot-back-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("forgot.back")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForgotPassword;
